"use client"

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import type { ActionConfiguration, WorkflowItem } from '@/types/workflow';

interface ActionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: ActionConfiguration;
  items: WorkflowItem[];
  onConfirm: (actionCode: string, items: any[], notes?: string) => Promise<void>;
  language: string;
}

export function ActionConfirmationModal({
  isOpen,
  onClose,
  action,
  items,
  onConfirm,
  language
}: ActionConfirmationModalProps) {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal caption is entity-aware. The "Worker: <name>" framing only makes
  // sense for rows that have a worker (WorkOrder, ClientRequest, VmsRequest).
  // VmsEmail rows don't yet have a parsed worker until processing completes
  // — and even then it's optional — so we fall back to the email subject so
  // the caption identifies the row meaningfully. Same idea for requests with
  // missing worker info: prefer the external request id over a "Unknown
  // Worker" string that's noise.
  //
  // Multi-row bulk: just the count, no per-row label.
  const { captionPrefix, captionValue } = (() => {
    if (items.length !== 1) {
      return { captionPrefix: 'Workers', captionValue: `${items.length} selected` };
    }
    const it = items[0] as any;
    const docType = (it.documentType || it.DocumentType || '').toString().toLowerCase();
    const subject = (it.subject || it.Subject || '').toString().trim();
    const worker = (it.workerFullName || '').toString().trim();
    const externalRequestId = (
      it.parsedExternalRequestId
      || it.ParsedExternalRequestId
      || it.vmsExternalRequestId
      || it.VmsExternalRequestId
      || ''
    ).toString().trim();

    if (docType === 'vmsemail') {
      return {
        captionPrefix: 'Email',
        captionValue: subject || worker || externalRequestId || (it.id || 'Unknown'),
      };
    }
    if (docType === 'vmsrequest' || docType === 'clientrequest') {
      return {
        captionPrefix: 'Request',
        captionValue: worker || externalRequestId || subject || (it.id || 'Unknown'),
      };
    }
    return {
      captionPrefix: 'Worker',
      captionValue: worker || 'Unknown Worker',
    };
  })();

  const actionTitle = language === 'fr' ? action.titleFrench : action.titleEnglish;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      // Pass the full items array (not just IDs) so the API hook can extract the correct IDs
      await onConfirm(action.actionCode, items, notes || undefined);
      setNotes(''); // Reset notes
      onClose();
    } catch (error) {
      console.error('Failed to execute action:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setNotes(''); // Reset notes on close
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isSubmitting) handleClose(); }}>
      <DialogContent className="max-w-lg">
        {/* Loading overlay */}
        {isSubmitting && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-500 border-t-transparent"></div>
              <span className="text-gray-700 font-medium">Processing...</span>
            </div>
          </div>
        )}
        
        <DialogHeader>
          <DialogTitle className="text-blue-900 font-semibold text-base">
            {captionPrefix}: {captionValue}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <h3 className="font-semibold text-lg mb-2">
              Confirm Action: {actionTitle}
            </h3>
            <p className="text-gray-600 text-sm">
              Are you sure you want to proceed with this action? This cannot be undone.
            </p>
          </div>

          {action.allowNotes && (
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Notes (Optional)
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes or comments here..."
                rows={4}
                disabled={isSubmitting}
                className="resize-none"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="elliptical-outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="elliptical"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Proceed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
